import {
  useRef,
  useEffect,
  forwardRef,
  FunctionComponent,
  ReactNode,
  PropsWithoutRef,
  Ref,
  MouseEvent,
  MouseEventHandler
} from 'react';
import styled, { css } from 'styled-components';
import { mix, readableColor } from 'polished';

import { BaseProps, ForwardProps, PropsWithDefaults, AsProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils';
import { getHoverColors, omitProps } from '../../styles';
import { useElement, useConsolidatedRef } from '../../hooks';
import Tooltip from '../Tooltip';
import Progress, { StyledProgressRing } from '../Progress';
import { StyledBackdrop } from '../Backdrop';
import { StyledIcon } from '../Icon';
import { StyledPopover } from '../Popover';

export interface ButtonProps extends BaseProps, AsProp {
  /** Text or content for the Button. */
  children: ReactNode;
  /**
   * Determines if the Button will be disabled.
   * @default false
   */
  disabled?: boolean;
  /** A location to navigate to. Passing an href will render an anchor styled as a Button. */
  href?: string;
  /**
   * Controls the styling of the Button.
   * @default "secondary"
   */
  variant?: 'primary' | 'secondary' | 'simple' | 'link' | 'text';
  /**
   * Controls the behavior of a Button within a Form.
   * @default "button"
   */
  type?: 'button' | 'reset' | 'submit';
  /**
   * Set the Icon prop to `true` if you're using just an Icon in your Button. Make sure to pass an Icon as children if `true`.
   * @default false
   */
  icon?: boolean;
  /**
   * Used for a smaller sized button.
   * @default false
   */
  compact?: boolean;
  /**
   * The label text will be used inside a Tooltip.
   */
  label?: string;
  /**
   * The aria label text will be inserted as the aria-label on the Button.
   */
  'aria-label'?: string;
  /**
   * Optionally renders an indeterminate progress indicator in a button.
   * @default false
   */
  loading?: boolean;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLButtonElement>;
}

type ButtonPropsWithDefaults = PropsWithDefaults<
  ButtonProps,
  'variant' | 'type' | 'disabled' | 'icon' | 'loading'
>;

export const StyledButton = styled.button.withConfig<ButtonPropsWithDefaults>(omitProps('loading'))(
  ({
    variant,
    icon,
    loading,
    compact,
    theme: {
      base: {
        spacing,
        'border-radius': baseBorderRadius,
        palette: { 'primary-background': backgroundColor, 'foreground-color': textColor },
        'hit-area': {
          'mouse-min': hitAreaMouse,
          'finger-min': hitAreaFinger,
          'compact-min': hitAreaCompact
        },
        animation: {
          speed,
          timing: { ease }
        },
        'disabled-opacity': disabledOpacity
      },
      components: {
        button: {
          color,
          'secondary-color': secondaryColor,
          padding,
          'border-radius': borderRadius,
          'border-width': borderWidth,
          'focus-shadow': focusShadow,
          touch: { padding: touchPadding }
        },
        link: { color: linkColor }
      }
    }
  }) => {
    const borderColor = variant === 'secondary' ? color : 'transparent';
    const contrastColor = tryCatch(() => readableColor(color));

    const hoverColors = getHoverColors(color);
    const hoverPrimaryColor = hoverColors.background;
    const hoverContrastColor = hoverColors.foreground;

    const secondaryHoverColor = tryCatch(() => mix(0.85, secondaryColor, color));

    let buttonColor = contrastColor;
    let buttonBackgroundColor = color;
    if (variant === 'secondary') {
      buttonColor = color;
      buttonBackgroundColor = secondaryColor;
    } else if (variant === 'simple') {
      if (icon) {
        buttonColor = 'currentColor';
        buttonBackgroundColor = 'transparent';
      } else {
        buttonColor = color;
        buttonBackgroundColor = backgroundColor;
      }
    } else if (variant === 'link') {
      buttonColor = linkColor;
      buttonBackgroundColor = 'transparent';
    } else if (variant === 'text') {
      buttonColor = textColor;
      buttonBackgroundColor = 'transparent';
    }

    return css`
      color: ${buttonColor};
      background-color: ${buttonBackgroundColor};
      display: ${variant === 'link' || variant === 'text' ? 'inline' : 'inline-flex'};
      align-items: center;
      outline: none;
      text-decoration: none;
      transition: all calc(0.5 * ${speed}) ${ease};
      cursor: pointer;

      ${variant !== 'link' && variant !== 'text'
        ? css`
            justify-content: center;
            line-height: 1;
            border: ${borderWidth} solid ${borderColor};
            position: relative;
          `
        : css`
            text-align: start;
            border: none;

            > ${StyledIcon} {
              vertical-align: bottom;
            }
          `}

      & + & {
        margin-inline-start: ${spacing};
      }

      /* Not able to combine with selector above. Stylis bug? */
      & + ${StyledPopover} + & {
        margin-inline-start: ${spacing};
      }

      @media (hover: hover) {
        &:hover {
          ${variant === 'primary' &&
          css`
            background-color: ${hoverPrimaryColor};
            color: ${hoverContrastColor};
            text-decoration: none;
          `}

          ${(variant === 'secondary' || (variant === 'simple' && !icon)) &&
          css`
            background-color: ${secondaryHoverColor};
            text-decoration: none;
          `}

          ${variant === 'simple' &&
          icon &&
          css`
            ::before {
              content: '';
              position: absolute;
              top: calc(${borderWidth} * -1);
              bottom: calc(${borderWidth} * -1);
              left: calc(${borderWidth} * -1);
              right: calc(${borderWidth} * -1);
              border-radius: inherit;
              background-color: currentColor;
              opacity: 0.15;
            }
          `}

          ${(variant === 'link' || variant === 'text') &&
          css`
            text-decoration: underline;
          `}
        }
      }

      ${variant !== 'link' &&
      variant !== 'text' &&
      css`
        min-height: ${hitAreaMouse};
        min-width: ${hitAreaMouse};
        border-radius: calc(${baseBorderRadius} * ${borderRadius});
        -webkit-user-select: none;
        user-select: none;
      `}

      ${compact &&
      css`
        min-height: ${hitAreaCompact};
        min-width: ${hitAreaCompact};
      `}

      ${!icon &&
      variant !== 'link' &&
      variant !== 'text' &&
      css`
        padding: ${padding};
      `}

      ${variant !== 'link' &&
      variant !== 'text' &&
      css`
        @media (pointer: coarse) {
          ${!compact &&
          css`
            min-height: ${hitAreaFinger};
            min-width: ${hitAreaFinger};
          `}
          border-radius: calc(${baseBorderRadius} * ${borderRadius});
          ${!icon &&
          css`
            padding: ${touchPadding};
          `}
        }
      `}

      &:disabled,
      &[disabled] {
        opacity: ${disabledOpacity};
        cursor: not-allowed;
        pointer-events: none;
      }

      &:enabled:focus,
      &:not([disabled]):focus {
        box-shadow: ${focusShadow};
      }

      ${icon &&
      variant !== 'link' &&
      variant !== 'text' &&
      css`
        border-radius: calc(${baseBorderRadius} * ${borderRadius});
        > svg {
          display: block;
        }
      `}

      ${loading &&
      css`
        ${StyledBackdrop} {
          background-color: ${buttonBackgroundColor};
          border-radius: inherit;
        }
        ${StyledProgressRing} {
          width: 1em;
          height: 1em;
          circle:nth-child(2) {
            animation-duration: calc(${speed} * 2);
          }
        }
      `}
    `;
  }
);

StyledButton.defaultProps = defaultThemeProp;

const Button: FunctionComponent<ButtonProps & ForwardProps> = forwardRef(
  (
    {
      variant = 'secondary',
      type = 'button',
      disabled = false,
      icon = false,
      compact = false,
      href,
      as,
      forwardedAs,
      label,
      'aria-label': ariaLabel,
      loading = false,
      children,
      ...restProps
    }: PropsWithoutRef<ButtonProps> & ForwardProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const [buttonEl, setButtonEl] = useElement();
    const mouseDownEvent = useRef<MouseEvent>();
    const buttonRef = useConsolidatedRef<HTMLButtonElement>(ref, setButtonEl);
    const showProgress = loading && variant !== 'link' && variant !== 'text';

    useEffect(() => {
      return () => {
        mouseDownEvent.current = undefined;
      };
    }, []);

    return (
      <>
        <StyledButton
          {...restProps}
          ref={buttonRef}
          as={as || (href ? 'a' : 'button')}
          forwardedAs={forwardedAs || (href ? 'a' : 'button')}
          variant={variant}
          icon={icon}
          compact={compact}
          type={href ? undefined : type}
          href={href}
          disabled={disabled && !href}
          loading={showProgress}
          aria-label={ariaLabel || label}
          onMouseDown={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
            (restProps.onMouseDown as MouseEventHandler | undefined)?.(e);
            e.persist();
            mouseDownEvent.current = e;
          }}
          onMouseUp={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
            const shouldFocus = mouseDownEvent.current && !mouseDownEvent.current.defaultPrevented;
            mouseDownEvent.current = undefined;

            if (shouldFocus && buttonRef.current !== document.activeElement) {
              buttonRef.current?.focus();
            }

            return (restProps.onMouseUp as MouseEventHandler | undefined)?.(e);
          }}
        >
          {showProgress && <Progress variant='ring' placement='local' />}
          {children}
        </StyledButton>
        {buttonEl && label && (
          <Tooltip target={buttonEl} showDelay='none' hideDelay='none' describeTarget={false}>
            {label}
          </Tooltip>
        )}
      </>
    );
  }
);

export default Button;
